import { localize } from "client_legacy/ndaw/culture";
import {
	HubDetailsLayout,
	MediaContentModel
} from "../../HubDetailsLayout";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { isNil, isNotNil } from "client/Misc/utils";
import {
	ReactElement,
	useMemo
} from "react";
import { ActiveFilter, ImageUrlWithAttribution } from "../../common";
import { AnnouncementStatisticsModel } from "client/Api/Hub/Announcements/announcementViewer";
import {
	TruncatingList,
	DateString,
	TagList,
	TagListDialog,
	TagModel,
	PipeSeparator
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ContentViewer,
	ContentViewerEmptyBlockSettings,
	HubItemLayoutContentViewerModel
} from "../../Builder/Viewer/ContentViewer";
import { AnnouncementActions } from "../AnnouncementActions/AnnouncementActions";
import { LinkedDocumentsViewer } from "../../LinkedDocumentsViewer";
import { LinkedDocumentPreviewModel, LinkedDocumentsPreview } from "../../LinkedDocumentsPreview";
import { HubLinkedDocumentViewModel } from "client/Api/Hub/common";
import { never } from "client_legacy/ndaw/errors";
import {
	ContentTitle,
	HubItemNameOnlyCategory,
	isFullCategoryModel,
	resolveTitle,
	resolveTitleKind
} from "../../Content/common";
import { isValidDate } from "client/DateAndTime/dateTimeUtils";
import { useToggle } from "client/Hooks";
import styles from "./AnnouncementContent.module.less";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";

export type AnnouncementContentStatisticsProps = ConditionalProps<
	{
		statistics: AnnouncementStatisticsModel;
		onStatisticsRetrieved: (statistics: AnnouncementStatisticsModel) => void;
		onOpenReactionsDialog: () => void;
		onOpenViewersDialog?: () => void;
	},
	"hasStatistics"
>;

export type AnnouncementContentDataProps = ConditionalProps<
	{
		title: ContentTitle;
		isPinned: boolean;
		categories: readonly HubItemCategoryModel[] | [HubItemNameOnlyCategory];
		selectedCategoryId?: number | null;
		publicationDate: Date | string;
		authorId: number | null;
		authorName: string | null;
		media: MediaContentModel;
		layout: HubItemLayoutContentViewerModel;
		tags: readonly TagModel<number>[];
		isEditable: boolean;
		areCommentsEnabled: boolean;
		emptyBlockSettings?: ContentViewerEmptyBlockSettings;
	},
	"hasData"
>;

export type AnnouncementContentModeProps = {
	mode: "full";
	activeFilter: ActiveFilter | null;
	onBack: () => void;
	onEdit: () => void;
	onSelectCategory: (categoryId: number) => void;
	onSelectAuthor: (authorId: number, authorName: string) => void;
	onTagClick: (tag: number | null) => void;
} | {
	mode: "details-only";
	onSelectCategory?: (categoryId: number) => void;
};

export type AnnouncementContentLinkedDocumentsModeProps =
	| {
		linkedDocumentsMode: "preview";
		linkedDocuments: readonly LinkedDocumentPreviewModel[];
		onChangeIsLoading?: undefined;
	}
	| {
		linkedDocumentsMode: "view";
		linkedDocuments: readonly HubLinkedDocumentViewModel[];
		onChangeIsLoading: (isLoading: boolean) => void;
	};

export type AnnouncementContentProps =
	& {
		timezone: TimeZone;
		shouldNotRenderEmptyFooter?: boolean;
		onViewImage?: (image: ImageUrlWithAttribution) => void;
	}
	& AnnouncementContentDataProps
	& AnnouncementContentModeProps
	& AnnouncementContentStatisticsProps
	& AnnouncementContentLinkedDocumentsModeProps;

export const AnnouncementContent = (props: AnnouncementContentProps): ReactElement => {
	const {
		title,
		isPinned = false,
		categories,
		selectedCategoryId,
		mode,
		hasData,
		publicationDate,
		authorId,
		authorName,
		media,
		layout,
		emptyBlockSettings,
		statistics,
		hasStatistics,
		onStatisticsRetrieved,
		onViewImage,
		tags,
		onOpenReactionsDialog,
		onOpenViewersDialog,
		areCommentsEnabled,
		isEditable,
		onSelectCategory,
		linkedDocuments,
		linkedDocumentsMode,
		onChangeIsLoading,
		shouldNotRenderEmptyFooter = false,
		timezone
	} = props;

	const renderLinkedDocuments = (): ReactElement | null => {
		const count: number = linkedDocuments.length;

		if (count === 0) {
			return null;
		}

		switch (linkedDocumentsMode) {
			case "preview":
				return (
					<HubDetailsLayout.LinkedDocuments count={count}>
						<LinkedDocumentsPreview
							items={linkedDocuments}
						/>
					</HubDetailsLayout.LinkedDocuments>
				);
			case "view":
				return (
					<HubDetailsLayout.LinkedDocuments count={count}>
						<LinkedDocumentsViewer
							items={linkedDocuments}
							onChangeIsLoading={onChangeIsLoading}
						/>
					</HubDetailsLayout.LinkedDocuments>
				);
			default:
				never(linkedDocumentsMode);
		}
	};

	const selectedCategoryName: string | nil = isNotNil(selectedCategoryId)
		? categories
			?.find(
				x => isFullCategoryModel(x)
					&& x.id === selectedCategoryId
			)
			?.name
		: null;

	return (
		<HubDetailsLayout>
			{mode === "full"
				? (
					<>
						<HubDetailsLayout.BackButton
							selectedCategoryName={selectedCategoryName ?? localize("_AllAnnouncements")}
							onBack={props.onBack}
							activeFilter={props.activeFilter}
						/>
						{hasData && (
							<HubDetailsLayout.Title
								onEdit={isEditable
									? props.onEdit
									: undefined
								}
								text={resolveTitle(title)}
								kind={resolveTitleKind(title)}
								isPinned={isPinned}
							/>
						)}
					</>
				)
				: hasData && (
					<HubDetailsLayout.SimpleTitle
						text={resolveTitle(title)}
						kind={resolveTitleKind(title)}
						isPinned={isPinned}
					/>
				)
			}
			{hasData && (
				<>
					<HubDetailsLayout.Subtitle>
						<HubDetailsLayout.SubtitleText shouldNotShrink>
							{isValidDate(publicationDate)
								? (
									<DateString
										value={publicationDate}
										format="RelativeTimestamp"
										timezone={timezone}
									/>
								)
								: localize("_NoValidInput")
							}
						</HubDetailsLayout.SubtitleText>
						{isNotNil(authorId) && authorName && (
							<>
								<PipeSeparator />
								<HubDetailsLayout.SubtitleLink
									trackingId="author"
									onClick={mode === "full"
										? () => props.onSelectAuthor(authorId, authorName!)
										: undefined
									}
									tooltip={authorName}
								>
									{authorName}
								</HubDetailsLayout.SubtitleLink>
							</>
						)}
						{categories.length > 0 && (
							<>
								<PipeSeparator />
								<div className={styles.subtitleCategories}>
									<AnnouncementCategoryList
										categories={categories}
										onSelectCategory={onSelectCategory}
									/>
								</div>
							</>
						)}
					</HubDetailsLayout.Subtitle>

					<HubDetailsLayout.Media
						media={media}
						hubItemKind={HubItemKind.Announcement}
						onViewImage={onViewImage}
					/>

					<HubDetailsLayout.Content>
						<ContentViewer
							hubItemKind={HubItemKind.Announcement}
							layout={layout}
							onViewImage={onViewImage}
							emptyBlockSettings={emptyBlockSettings}
						/>
					</HubDetailsLayout.Content>
					{renderLinkedDocuments()}
					{(!shouldNotRenderEmptyFooter
						|| hasStatistics
						|| tags.length > 0)
						&& (
							<HubDetailsLayout.Footer
								stats={hasStatistics
									? {
										reactions: {
											value: statistics.reactionCounters,
											onClick: statistics.reactionCounters.length > 0
												? onOpenReactionsDialog
												: undefined,
										},
										views:{
											value: statistics.viewsCount,
											onClick: statistics.viewsCount > 0
												? onOpenViewersDialog
												: undefined
										}
									}
									: undefined
								}
								rightSide={(
									<TagList
										trackingId="tag-list"
										tags={tags}
										onTagClick={
											props.mode === "full"
												? props.onTagClick
												: undefined
										}
										alignment="right"
										showMoreTagsColor="secondary"
									/>
								)}
							>
								{hasStatistics && (
									<AnnouncementActions
										announcementId={statistics.announcementId}
										statistics={statistics}
										onStatisticsRetrieved={onStatisticsRetrieved}
										areCommentsEnabled={areCommentsEnabled}
									/>
								)}
							</HubDetailsLayout.Footer>
						)
					}
				</>
			)}
		</HubDetailsLayout>
	);
};
AnnouncementContent.displayName = "AnnouncementContent";

type AnnouncementCategoryListProps = {
	categories: readonly HubItemCategoryModel[] | [HubItemNameOnlyCategory];
	onSelectCategory?: (categoryId: number) => void;
};

const AnnouncementCategoryList = (props: AnnouncementCategoryListProps): ReactElement => {
	const {
		categories,
		onSelectCategory
	} = props;

	const [isTagListDialogVisible, toggleTagListVisibility] = useToggle(false);

	const tags = useMemo(
		(): readonly TagModel<number | undefined>[] => {
			return [...categories].sort(
				(a, b): number => {
					return nativeSortFunction(
						a.name,
						b.name,
						DataSourceSortDirection.Ascending
					);
				}
			).map(
				(
					x: HubItemCategoryModel | HubItemNameOnlyCategory
				): TagModel<number | undefined> => ({
					text: x.name,
					value: isFullCategoryModel(x) ? x.id : undefined,
					color: "secondary"
				})
			);
		},
		[categories]
	);

	const tagClickHandler = useMemo(
		(): ((value: number | undefined) => void) | undefined => {
			if (isNil(onSelectCategory)) {
				return undefined;
			}

			return (value: number | undefined): void => {
				if (isNil(value)) {
					return;
				}

				onSelectCategory(value);
			};
		},
		[onSelectCategory]
	);

	return (
		<>
			<TruncatingList
				items={categories}
				trackingId="category-list"
				maxNumberOfVisibleItems={3}
				wrappingBehavior="ellipsis"
				hiddenCounter={{
					onClick: toggleTagListVisibility
				}}
				shouldShowAtLeastOne
			>
				{(item: HubItemCategoryModel | HubItemNameOnlyCategory): ReactElement => (
					<HubDetailsLayout.SubtitleLink
						key={item.name}
						trackingId="category"
						onClick={isNotNil(onSelectCategory) && isFullCategoryModel(item)
							? () => onSelectCategory(item.id)
							: undefined
						}
						shouldNotWrap
					>
						{item.name}
					</HubDetailsLayout.SubtitleLink>
				)}
			</TruncatingList>
			{isTagListDialogVisible && (
				<TagListDialog
					trackingId="categories-dialog"
					tags={tags}
					onClose={toggleTagListVisibility}
					title={localize("_Categories")}
					onTagClick={tagClickHandler}
				/>
			)}
		</>
	);
};

AnnouncementCategoryList.displayName = "AnnouncementCategoryList";